import type {
  BuyerResearchBriefDto,
  BuyerResearchCandidateListQuery,
  BuyerResearchCandidateSort,
  BuyerResearchMoneyField,
} from "@/api/buyerResearch";
import type { ListFilterDef, ListFilters } from "@/hooks/useListFilters";
import { parseMoneyInput } from "./researchFormat";

/**
 * The research candidate list's URL state (R20) — every filter, the sort, the
 * page and the page size, each under an `r…` key so it cannot collide with a
 * sibling on the buyer record page.
 *
 * <p>🔴 <b>THIS SURFACE PAGES IN THE URL, AND THE MODULE'S RECORD-PAGE
 * PRECEDENT SAYS NOT TO.</b> `recordSubmissions.tsx` keeps its pager out of the
 * query string on a record page; R20 reverses that for this one list, because a
 * run's candidates are now filtered, sorted and paged by the SERVER and a
 * reload or a Back that dropped the reader onto page 1 of a different filter
 * would lose their place in a list of hundreds. That file's comment says so.</p>
 *
 * <p>🔴 <b>EVERY KEY HERE MUST ALSO BE IN {@link RESEARCH_LIST_PARAMS}</b>, which
 * `BuyerDetailPage` deletes on a tab change and when it opens a submission. A
 * key missing from that list follows the reader onto the next tab.</p>
 *
 * <p>Defaults stay out of the URL: every `serialize` returns null at its
 * default, so a list at rest has a clean query string.</p>
 */

/** The page parameter the list's `useListFilters` writes (R12 option). */
export const RESEARCH_PAGE_PARAM = "rPage";

/** Fit threshold chips. The string form is the URL token. */
export type ResearchFitThreshold = "any" | "50" | "75";

/** The default threshold: 50+ (R6's lowest coloured band). */
export const DEFAULT_FIT_THRESHOLD: ResearchFitThreshold = "50";

/** Page sizes the footer offers. 25 is the server's own default. */
export const RESEARCH_PAGE_SIZES = [25, 50, 100] as const;
export type ResearchPageSize = (typeof RESEARCH_PAGE_SIZES)[number];
export const DEFAULT_RESEARCH_PAGE_SIZE: ResearchPageSize = 25;

/** The canonical order of the money fields — the server's attribution order. */
export const MONEY_FIELDS: readonly BuyerResearchMoneyField[] = ["asking", "cashFlow", "revenue"];
const MONEY_FIELD_SET = new Set<string>(MONEY_FIELDS);

const NO_FIELDS: readonly BuyerResearchMoneyField[] = [];

function def<V>(d: ListFilterDef<V>): ListFilterDef<V> {
  return d;
}

/**
 * A money bound: a number, or null when unset. Junk in the URL reads as unset.
 * Debounced like the search box, so typing "150000" is one request rather than
 * six.
 */
const moneyBound = () =>
  def<number | null>({
    control: "hidden",
    defaultValue: null,
    debounceMs: 400,
    parse: (raw) => {
      const n = parseMoneyInput(raw);
      return n == null ? undefined : n;
    },
    serialize: (v) => (v == null ? null : String(v)),
  });

/** A flag: `1` in the URL, absent otherwise. */
const flag = () =>
  def<boolean>({
    control: "hidden",
    defaultValue: false,
    parse: (raw) => (raw === "1" ? true : undefined),
    serialize: (v) => (v ? "1" : null),
  });

export const RESEARCH_LIST_FILTERS = {
  rQ: def<string>({
    control: "search",
    defaultValue: "",
    // Without this every keystroke is a round-trip to the server.
    debounceMs: 300,
  }),
  rFit: def<ResearchFitThreshold>({
    control: "hidden",
    defaultValue: DEFAULT_FIT_THRESHOLD,
    parse: (raw) => (raw === "any" || raw === "50" || raw === "75" ? raw : undefined),
    serialize: (v) => (v === DEFAULT_FIT_THRESHOLD ? null : v),
  }),
  rAskMin: moneyBound(),
  rAskMax: moneyBound(),
  rCfMin: moneyBound(),
  rCfMax: moneyBound(),
  rRevMin: moneyBound(),
  rRevMax: moneyBound(),
  rMissing: def<readonly BuyerResearchMoneyField[]>({
    control: "hidden",
    defaultValue: NO_FIELDS,
    // Canonical order and no duplicates, so two URLs that mean the same thing
    // send the same request and share one cache entry.
    parse: (raw) => {
      const asked = new Set(raw.split(",").map((t) => t.trim()).filter((t) => MONEY_FIELD_SET.has(t)));
      const fields = MONEY_FIELDS.filter((f) => asked.has(f));
      return fields.length > 0 ? fields : undefined;
    },
    serialize: (v) => (v.length === 0 ? null : MONEY_FIELDS.filter((f) => v.includes(f)).join(",")),
  }),
  rDismissed: flag(),
  rLatest: flag(),
  // Leave out rows attached to the buyer. Off by default: attached rows stay in
  // their sorted place, labelled. Every run kind.
  rHideAttached: flag(),
  // Quick search only: merge in the rows that could not be confirmed / did not
  // match what was typed. Ignored by the server on an ordinary run.
  rUnconfirmed: flag(),
  rNoMatch: flag(),
  rSort: def<BuyerResearchCandidateSort>({
    control: "hidden",
    defaultValue: "fit",
    parse: (raw) =>
      raw === "fit" || raw === "asking" || raw === "cashFlow" || raw === "revenue" ? raw : undefined,
    serialize: (v) => (v === "fit" ? null : v),
  }),
  rDir: def<"asc" | "desc">({
    control: "hidden",
    defaultValue: "desc",
    parse: (raw) => (raw === "asc" || raw === "desc" ? raw : undefined),
    serialize: (v) => (v === "desc" ? null : v),
  }),
  rSize: def<ResearchPageSize>({
    control: "hidden",
    defaultValue: DEFAULT_RESEARCH_PAGE_SIZE,
    parse: (raw) => {
      const n = Number(raw);
      return (RESEARCH_PAGE_SIZES as readonly number[]).includes(n) ? (n as ResearchPageSize) : undefined;
    },
    serialize: (v) => (v === DEFAULT_RESEARCH_PAGE_SIZE ? null : String(v)),
  }),
} as const;

/**
 * M2 — the same list on a QUICK SEARCH run, where the score chip defaults to
 * Any. A quick search's default list already shows only what matched the typed
 * sentence, and the stated match count must equal the rows shown; a fit
 * threshold applied on top by default would make the two disagree.
 *
 * <p>Same keys, so the type is the same and `RESEARCH_LIST_PARAMS` covers both.
 * Only the default moves: a chip the reader sets is written to the URL and kept
 * (`rFit=50` / `rFit=75`), and "Any" is the default so it stays out of it.</p>
 */
export const QUICK_RESEARCH_LIST_FILTERS: ResearchListFilterDefs = {
  ...RESEARCH_LIST_FILTERS,
  rFit: def<ResearchFitThreshold>({
    control: "hidden",
    defaultValue: "any",
    parse: (raw) => (raw === "any" || raw === "50" || raw === "75" ? raw : undefined),
    serialize: (v) => (v === "any" ? null : v),
  }),
};

/** The list's filter descriptor for a run of this kind. */
export function researchListFiltersFor(quick: boolean): ResearchListFilterDefs {
  return quick ? QUICK_RESEARCH_LIST_FILTERS : RESEARCH_LIST_FILTERS;
}

export type ResearchListFilterDefs = typeof RESEARCH_LIST_FILTERS;
export type ResearchListFilters = ListFilters<ResearchListFilterDefs>;
export type ResearchListValues = ResearchListFilters["values"];

/**
 * Every URL key this list owns, the page included. `BuyerDetailPage` deletes
 * all of them at both of its tab-leaving sites (R20); a key the list writes and
 * this array omits would follow the reader onto the next tab.
 */
export const RESEARCH_LIST_PARAMS: readonly string[] = [
  ...Object.keys(RESEARCH_LIST_FILTERS),
  RESEARCH_PAGE_PARAM,
];

/** The URL keys of each money field's bounds. */
export const MONEY_BOUND_KEYS: Record<
  BuyerResearchMoneyField,
  { min: "rAskMin" | "rCfMin" | "rRevMin"; max: "rAskMax" | "rCfMax" | "rRevMax" }
> = {
  asking: { min: "rAskMin", max: "rAskMax" },
  cashFlow: { min: "rCfMin", max: "rCfMax" },
  revenue: { min: "rRevMin", max: "rRevMax" },
};

/** Whether a range is set on this field — the server filters on it only then. */
export function moneyFieldFiltered(values: ResearchListValues, field: BuyerResearchMoneyField): boolean {
  const keys = MONEY_BOUND_KEYS[field];
  return values[keys.min] != null || values[keys.max] != null;
}

/**
 * The request the list sends. Server defaults stay OFF the wire, so a list at
 * rest issues the same request every time; the client's own default of 50+ is
 * NOT the server's (absent = Any), so `minFit` is sent at the default.
 */
export function researchListQuery(values: ResearchListValues, page: number): BuyerResearchCandidateListQuery {
  const q: BuyerResearchCandidateListQuery = {};
  if (values.rFit !== "any") q.minFit = Number(values.rFit);
  if (values.rAskMin != null) q.askMin = values.rAskMin;
  if (values.rAskMax != null) q.askMax = values.rAskMax;
  if (values.rCfMin != null) q.cfMin = values.rCfMin;
  if (values.rCfMax != null) q.cfMax = values.rCfMax;
  if (values.rRevMin != null) q.revMin = values.rRevMin;
  if (values.rRevMax != null) q.revMax = values.rRevMax;
  if (values.rMissing.length > 0) q.showMissing = values.rMissing.join(",");
  if (values.rDismissed) q.dismissed = true;
  if (values.rLatest) q.latestOnly = true;
  if (values.rHideAttached) q.hideAttached = true;
  if (values.rUnconfirmed) q.showUnconfirmed = true;
  if (values.rNoMatch) q.showNoMatch = true;
  const search = values.rQ.trim();
  if (search) q.q = search;
  if (values.rSort !== "fit") {
    q.sort = values.rSort;
    // `dir` is ignored for Best fit, so it rides only a money sort, and only
    // when it is not the server's own default.
    if (values.rDir !== "desc") q.dir = values.rDir;
  }
  if (page > 1) q.page = page;
  if (values.rSize !== DEFAULT_RESEARCH_PAGE_SIZE) q.pageSize = values.rSize;
  return q;
}

/**
 * Whether anything a reader can set is off its default. Reads `inputs`, so a
 * half-typed search counts — the reader typing in the box must keep it on
 * screen (§5). Compared by URL TOKEN rather than by value, so the money-fields
 * array compares by content.
 */
export function researchFiltersActive(filters: ResearchListFilters): boolean {
  const inputs = filters.inputs as Record<string, unknown>;
  const defs = filters.defs;
  return (Object.keys(defs) as (keyof ResearchListFilterDefs)[]).some((key) => {
    const d = defs[key] as ListFilterDef<unknown>;
    const value = inputs[key];
    if (key === "rQ") return typeof value === "string" && value.trim() !== "";
    return d.serialize ? d.serialize(value) !== null : !Object.is(value, d.defaultValue);
  });
}

/**
 * R16 — "Match the buyer's brief". Sets the four bounds the brief carries and
 * CLEARS the two it has no value for (the brief has no cash-flow or revenue
 * maximum), leaving the fit threshold alone. A brief bound that is null clears
 * that key too: the button matches the brief, it does not merge with it.
 */
export function briefBoundsWrite(
  brief: Pick<BuyerResearchBriefDto, "askingPriceMin" | "askingPriceMax" | "cashFlowMin" | "revenueMin">,
): Record<string, string | null> {
  const token = (n: number | null) => (n == null ? null : String(n));
  return {
    rAskMin: token(brief.askingPriceMin),
    rAskMax: token(brief.askingPriceMax),
    rCfMin: token(brief.cashFlowMin),
    rCfMax: null,
    rRevMin: token(brief.revenueMin),
    rRevMax: null,
  };
}

/** Whether the brief carries anything the button could apply. */
export function briefHasBounds(
  brief: Pick<BuyerResearchBriefDto, "askingPriceMin" | "askingPriceMax" | "cashFlowMin" | "revenueMin"> | null,
): boolean {
  if (!brief) return false;
  return (
    brief.askingPriceMin != null ||
    brief.askingPriceMax != null ||
    brief.cashFlowMin != null ||
    brief.revenueMin != null
  );
}
